import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Latest queued mutation per line-item KEY while a request is in flight (Padel USA perf). */
  #queuedByKey = new Map();

  /** Action of the in-flight cart mutation, or null when idle. */
  #inFlightAction = /** @type {string | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Resolve the row from the event target (the quantity selector), NOT the render-time
    // index — robust if an earlier optimistic removal has since shifted the rows.
    const lineItemRow =
      (event.target instanceof Element ? event.target.closest('.cart-items__table-row') : null) ??
      this.refs.cartItemRows[line - 1];

    if (quantity === 0) {
      return this.#removeLineItem(lineItemRow, line);
    }

    this.updateQuantity({
      line,
      key: lineItemRow instanceof HTMLElement ? lineItemRow.dataset.key : undefined,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal click (markup: on:click="/onLineItemRemove/{index}").
   * Resolves the row from the CLICKED element rather than the render-time index, so it stays
   * correct even after an earlier optimistic removal has shifted the rows.
   * @param {number} line - The render-time line index (fallback only).
   * @param {Event} [event] - The click event (its target is the remove button).
   */
  onLineItemRemove(line, event) {
    const target = event && event.target;
    const row =
      (target instanceof Element ? target.closest('.cart-items__table-row') : null) ??
      this.refs.cartItemRows[line - 1];

    this.#removeLineItem(/** @type {HTMLElement | undefined} */ (row), line);
  }

  /**
   * Removes one cart row: fires the (key-based) cart mutation and plays the optimistic
   * remove animation. Shared by the trash button and a stepper driven down to 0.
   * @param {HTMLElement | undefined} cartItemRowToRemove - The row to remove.
   * @param {number} line - The render-time line index (fallback only).
   */
  #removeLineItem(cartItemRowToRemove, line) {
    // Already animating out — ignore a repeat click so a removal is never double-sent.
    if (cartItemRowToRemove?.classList.contains('removing')) return;

    this.updateQuantity({
      line,
      key: cartItemRowToRemove?.dataset.key,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line index (fallback identifier / ref lookups).
   * @param {string} [config.key] - The stable line-item key (primary identifier).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    // Padel USA perf — every mutation targets a STABLE line-item key (config.key), never a
    // 1-based index, so a queued action can't hit the wrong line after an earlier removal
    // reindexes the cart. That lets the trash AND the steppers stay clickable at all times:
    // requests still serialize (one in flight; only the LATEST per key is queued) so morph
    // order stays correct, but the click registers and the optimistic UI fires immediately.
    if (this.#inFlightAction) {
      this.#queuedByKey.set(config.key ?? config.line, config);
      return;
    }
    this.#send(config);
  }

  /**
   * Sends one cart change request (queue-aware — see updateQuantity).
   * @param {Object} config - The config.
   * @param {number} config.line - The line index (fallback identifier / ref lookups).
   * @param {string} [config.key] - The stable line-item key (primary identifier).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  #send(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#inFlightAction = config.action;
    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      // Prefer the stable line-item key (`id`); fall back to the 1-based `line` only if a
      // row somehow had no data-key. `id` is immune to the reindexing a removal causes.
      ...(key ? { id: key } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    // Skip the long total shimmer when optimistic totals can take over instantly
    // (cart-addons.js recomputes from data-line-unit-price and data-merch).
    if (!document.querySelector('shipping-protection[data-merch]')) {
      cartTotal?.shimmer();
    }

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(config, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Intermediate responses of a coalesced burst skip the morph/dispatch — only the
        // FINAL response (queue empty) touches the DOM, so the user's newest input value
        // never gets flashed back to a stale server state mid-burst.
        if (this.#queuedByKey.size > 0) return;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        // Drain the coalesced queue: send the next latest mutation, keeping the flight
        // chain alive until the queue is empty. Each queued config carries its own stable
        // key, so draining after a removal that reindexed the cart still hits the right line.
        const queued = this.#queuedByKey.entries().next();
        if (!queued.done) {
          const [queuedKey, queuedConfig] = queued.value;
          this.#queuedByKey.delete(queuedKey);
          this.#send(queuedConfig);
          return;
        }
        this.#inFlightAction = null;
        this.#enableCartItems();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {Object} config - The failed mutation's config (carries `key` and `line`).
   * @param {string} [config.key] - The stable line-item key.
   * @param {number} config.line - The line index (fallback).
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (config, parsedResponseText) => {
    // Find the row by its stable key (the index may have shifted); fall back to the index.
    const row =
      (config.key && this.querySelector(`.cart-items__table-row[data-key="${config.key}"]`)) ||
      this.refs.cartItemRows[config.line - 1] ||
      null;

    // Reset the optimistic quantity back to the server value (no-op on a removal, which has
    // no input to reset).
    const quantityInput = row instanceof Element ? row.querySelector('input[data-cart-quantity]') : null;
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    // The error path never morphs, so an optimistic total written at click time would stay
    // wrong (e.g. availability-capped quantity). Tell the optimistic-totals listener
    // (cart-addons.js) to recompute from the now-reset inputs.
    this.dispatchEvent(new Event('cart:optimistic-resync', { bubbles: true }));

    const cartItemErrorContainer = row instanceof Element ? row.querySelector('.cart-items__error') : null;
    const cartItemError = row instanceof Element ? row.querySelector('.cart-item__error-text') : null;

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Marks the component as having an in-flight mutation. Key-based serialization (see
   * updateQuantity) makes concurrent clicks safe, so this NO LONGER blocks any control — it
   * is a non-blocking state hook only. The trash + steppers stay live at all times; a row
   * that is leaving is locked individually via its own `.removing` class (CSS).
   */
  #disableCartItems() {
    this.classList.add('cart-items-mutating');
  }

  /**
   * Clears the in-flight state marker.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-mutating');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
